 
/* eslint-disable @typescript-eslint/no-unused-vars */
import { Component, OnInit, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PromptDialogComponent } from '../prompt-dialog/prompt-dialog.component';
import { Prompt } from '@app-libs/model';
import { from } from 'rxjs';

// Angular Core Modules
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

// Angular Material Modules
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatChipsModule } from '@angular/material/chips';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { PromptsApiService } from '../../../services/api/prompts-api.service';

@Component({
  selector: 'app-prompt-list',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatDialogModule,
    MatSnackBarModule,
    MatSlideToggleModule,
    MatChipsModule,
    MatSelect,
    MatOption,
    MatCheckboxModule,
  ],
  templateUrl: './prompt-list.component.html',
  styleUrls: ['./prompt-list.component.scss'],
})
export class PromptListComponent implements OnInit {
  displayedColumns: string[] = [
    'name',
    'category',
    'tags',
    'permission_type',
    'status',
    'actions',
  ];
  dataSource: MatTableDataSource<Prompt> = new MatTableDataSource();
  isLoading = false;
  categories: string[] = [];
  tags: string[] = [];
  selectedCategory = '';
  selectedTag: string[] = [];
  searchQuery = ''; // Declare searchQuery for the search bar

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private promptsService: PromptsApiService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.loadPrompts();
  }

  /**
   * Load all prompts and bind to the data source.
   */
  loadPrompts(): void {
    this.isLoading = true;
    this.promptsService
      .fetchAllPrompts()
      .toPromise()
      .then((prompts: Prompt[] | undefined) => {
        if (!prompts) return;
        this.dataSource.data = prompts;
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
        this.isLoading = false;

        this.extractCategoriesAndTags(prompts);
      })
      .catch(() => {
        this.snackBar.open('Failed to load prompts.', 'Close', {
          duration: 3000,
        });
        this.isLoading = false;
      });
  }

  /** Extract unique categories and tags */
  extractCategoriesAndTags(prompts: Prompt[]): void {
    this.categories = [
      ...new Set(
        prompts
          .map((p) => p.category)
          .filter((category): category is string => !!category)
      ),
    ];
    this.tags = [...new Set(prompts.flatMap((p) => p.tags || []))];
  }

  /** Apply category filter */
  applyCategoryFilter(category: string): void {
    this.selectedCategory = category;
    this.filterData();
  }

  /** Apply tag filter */
  applyTagFilter(selectedTags: string[]): void {
    this.selectedTag = selectedTags;
    this.filterData();
  }

  /** Combined filter for category and tags */
  filterData(): void {
    this.dataSource.filterPredicate = (data: Prompt) => {
      const matchesCategory = this.selectedCategory
        ? data.category === this.selectedCategory
        : true;
      const matchesTags = this.selectedTag.length
        ? this.selectedTag.every((tag) => data.tags?.includes(tag))
        : true;
      return matchesCategory && matchesTags;
    };
    this.dataSource.filter = Math.random().toString(); // Force table refresh
  }

  /** Combined filter for search, category, and tags */
  applyFilter(): void {
    this.dataSource.filterPredicate = (data: Prompt, filter: string) => {
      const matchesCategory = this.selectedCategory
        ? data.category === this.selectedCategory
        : true;
      const matchesTags = this.selectedTag.length
        ? this.selectedTag.every((tag) => data.tags?.includes(tag))
        : true;
      const matchesSearch =
        data.name.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        data.category?.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        (data.tags || []).some((tag) =>
          tag.toLowerCase().includes(this.searchQuery.toLowerCase())
        );

      return matchesCategory && matchesTags && matchesSearch;
    };

    this.dataSource.filter = Math.random().toString(); // Force table refresh
  }

  /** Clear the search input */
  clearSearch(): void {
    this.searchQuery = '';
    this.applyFilter();
  }

  /**
   * Open dialog to create a new prompt.
   */
  createPrompt(): void {
    const dialogRef = this.dialog.open(PromptDialogComponent, {
      width: '500px',
      data: { mode: 'create' },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) this.loadPrompts();
    });
  }

  /**
   * Edit an existing prompt.
   */
  editPrompt(prompt: Prompt): void {
    const dialogRef = this.dialog.open(PromptDialogComponent, {
      width: '700px',
      data: { mode: 'edit', prompt },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) this.loadPrompts();
    });
  }

  /**
   * Delete a prompt by its ID.
   */
  deletePrompt(promptId: string): void {
    if (confirm('Are you sure you want to delete this prompt?')) {
      this.promptsService
        .deletePrompt(promptId)
        .toPromise()
        .then((success: boolean | undefined) => {
          if (success) {
            this.snackBar.open('Prompt deleted.', 'Close', { duration: 3000 });
            this.loadPrompts();
          } else {
            this.snackBar.open('Failed to delete prompt.', 'Close', {
              duration: 3000,
            });
          }
        })
        .catch(() => {
          this.snackBar.open('Failed to delete prompt.', 'Close', {
            duration: 3000,
          });
        });
    }
  }

  /**
   * Toggle activation status of a prompt.
   */
  toggleActivation(prompt: Prompt): void {
    from(
      this.promptsService.setPromptActiveStatus(
        prompt.prompt_id,
        !prompt.is_active
      )
    ).subscribe((success) => {
      if (success) {
        prompt.is_active = !prompt.is_active;
        const status = prompt.is_active ? 'activated' : 'deactivated';
        this.snackBar.open(`Prompt ${status}.`, 'Close', { duration: 3000 });
      } else {
        this.snackBar.open('Failed to update status.', 'Close', {
          duration: 3000,
        });
      }
    });
  }

  toggleSelectAll(event: any): void {
    const isChecked = event.checked;
    //TODO fix this
    // this.dataSource.data.forEach(prompt => (prompt.selected = isChecked));
  }

  /** View prompt details */
  viewPrompt(prompt: Prompt): void {
    this.dialog.open(PromptDialogComponent, {
      width: '600px',
      data: { mode: 'view', prompt },
    });
  }

  /** Duplicate an existing prompt */
  duplicatePrompt(prompt: Prompt): void {
    const duplicatedPrompt: Prompt = {
      ...prompt,
      prompt_id: '', // Clear ID for new entry
      name: `${prompt.name} (Copy)`,
    };

    const dialogRef = this.dialog.open(PromptDialogComponent, {
      width: '600px',
      data: { mode: 'create', prompt: duplicatedPrompt },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.snackBar.open('Prompt duplicated successfully.', 'Close', {
          duration: 3000,
        });
        this.loadPrompts();
      }
    });
  }

  /** Select/Deselect all prompts */
  // toggleSelectAll(event: any): void {
  //   const isChecked = event.checked;
  //   this.dataSource.data.forEach(prompt => (prompt.selected = isChecked));
  // }
}
